/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import axios, { isAxiosError } from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import { H2, H3, Label, LabelText, FilledTextField } from '@this/components/appoint/common';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import User, { convertUserResponseToArgs } from '@this/domain/user/user2';
import type { UserResponseArgs } from '@this/domain/user/user2';
import Dialog from '@material-ui/core/Dialog';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import AppointContext from '@this/components/appoint/appoint_context';
import Utils from '@this/domain/appoint/utils';
import { TheSmallTextField } from '../../users/sign_inup_form';

interface Response {
  organization_name: string;
  users: UserResponseArgs[];
}

const Members = observer(() => {
  const contextValue = useContext(AppointContext);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showInviteDialog, setShowInviteDialog] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [isInvitationSUbmitting, setIsInvitationSubmitting] = useState<boolean>(false);

  const fetchUsers = async () => {
    const res = await axios.get<Response>('/aicalendar/organization/members.json');
    setOrganizationName(res.data.organization_name);
    setUsers(res.data.users.map(u => new User(convertUserResponseToArgs(u))));
  };

  // マウント時に1回発動
  useEffect(() => {
    fetchUsers();
    Utils.trackEvent('[cal][view] organization members');
  }, []);

  const handleSubmit = async () => {
    if (!editingUser) {
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.put(`/aicalendar/organization/members/${editingUser.id}.json`, {
        last_name: editingUser.lastName,
        first_name: editingUser.firstName,
        aicalendar_available: editingUser.aicalendarAvailable,
        aicalendar_admin: editingUser.aicalendarAdmin
      });
      setEditingUser(null);
      await fetchUsers();
    } catch (e) {
      const errors = isAxiosError(e) && e.response?.data?.errors;
      const message =
        (Array.isArray(errors) ? errors.join('\n') : errors) ||
        'サーバーエラーが発生しました。時間をおいて再度お試しください';
      contextValue.setSnackbarContent({ message, severity: 'error' });
      contextValue.setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvite = async () => {
    setIsInvitationSubmitting(true);
    const params = {
      user: { email: inviteEmail }
    };
    await axios.post('/aicalendar/users/invitation', params);
    setIsInvitationSubmitting(false);
    setShowInviteDialog(false);
    contextValue.setSnackbarContent({
      message: `${inviteEmail} へ招待メール送りました。`,
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
    fetchUsers();
  };

  const renderUsersTable = () => (
    <TableContainer component={Paper}>
      <TheH3>{organizationName}</TheH3>
      <Table>
        <TableHead>
          <TableRow>
            <TheLeftTableCell>氏名</TheLeftTableCell>
            <TableCell>メールアドレス</TableCell>
            <TableCell>AIカレンダー利用権限</TableCell>
            <TableCell>AIカレンダー管理者権限</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(u => (
            <TableRow key={u.id}>
              <TheLeftTableCell>{u.name}</TheLeftTableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.aicalendarAvailable ? <CheckIcon /> : ''}</TableCell>
              <TableCell>{u.aicalendarAdmin ? <CheckIcon /> : ''}</TableCell>
              <TableCell align="right">
                <Button color="primary" onClick={() => setEditingUser(new User({ ...u }))}>
                  編集
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderEditUserDialog = () => (
    <Dialog open={editingUser !== null} fullWidth maxWidth="sm" onClose={() => setEditingUser(null)}>
      {editingUser && (
        <Box padding="27px 24px 24px">
          <TheModalH3>ユーザー情報編集</TheModalH3>
          <Label>
            <LabelText>氏名</LabelText>
          </Label>
          <Box display="flex">
            <TheSmallTextField
              placeholder="山田"
              value={editingUser.lastName}
              onChange={e => {
                editingUser.lastName = e.target.value;
              }}
              style={{ marginRight: '20px' }}
            />
            <TheSmallTextField
              placeholder="太郎"
              value={editingUser.firstName}
              onChange={e => {
                editingUser.firstName = e.target.value;
              }}
            />
          </Box>
          <Box>
            <Label>
              <LabelText>メールアドレス</LabelText>
            </Label>
            <FilledTextField placeholder="yamada-taro＠aitravel.jp" value={editingUser.email} disabled />
          </Box>
          <Box marginTop="6px">
            <FormControlLabel
              control={
                <Switch
                  checked={!!editingUser.aicalendarAvailable}
                  onChange={e => {
                    editingUser.aicalendarAvailable = e.target.checked;
                  }}
                  color="primary"
                />
              }
              label="AIカレンダー利用権限"
            />
          </Box>
          <Box marginTop="6px">
            <FormControlLabel
              control={
                <Switch
                  checked={!!editingUser.aicalendarAdmin}
                  onChange={e => {
                    editingUser.aicalendarAdmin = e.target.checked;
                  }}
                  color="primary"
                />
              }
              label="AIカレンダー管理者権限"
            />
          </Box>
          <Box marginTop="20px" textAlign="right">
            <Button color="primary" style={{ marginRight: '20px' }} onClick={() => setEditingUser(null)}>
              キャンセル
            </Button>
            <Button color="primary" variant="contained" onClick={() => handleSubmit()} disabled={isSubmitting}>
              保存
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );

  const renderUserInviteDialog = () => (
    <Dialog open={showInviteDialog} fullWidth maxWidth="sm" onClose={() => setShowInviteDialog(false)}>
      <Box padding="27px 24px 24px">
        <TheModalH3>新しいユーザーを追加</TheModalH3>
        <Description>招待するメールアドレスを入力してください</Description>
        <Box marginTop="32px">
          <Label>
            <LabelText>メールアドレス</LabelText>
          </Label>
          <FilledTextField
            placeholder="name@example.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            fullWidth
            style={{ marginBottom: '20px' }}
          />
        </Box>
        <Box marginTop="80px" textAlign="right">
          <Button color="primary" style={{ marginRight: '20px' }} onClick={() => setShowInviteDialog(false)}>
            キャンセル
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={isInvitationSUbmitting}
            onClick={() => handleInvite()}
          >
            招待メールを送信
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

  return (
    <TheContainer>
      <TheBox display="flex" alignItems="center" marginBottom="20px">
        <TheH2 style={{ marginRight: 'auto', marginBottom: '0' }}>ユーザー管理</TheH2>
        <Button variant="contained" color="primary" onClick={() => setShowInviteDialog(true)}>
          ＋ 新しいユーザーを招待
        </Button>
      </TheBox>
      {renderUsersTable()}
      {renderEditUserDialog()}
      {renderUserInviteDialog()}
    </TheContainer>
  );
});

const TheContainer = styled(Container)`
  max-width: 960px;
  margin: 0 auto;
`;

const TheBox = styled(Box)`
  margin-bottom: 32px;
`;

const TheH2 = styled(H2)`
  font-size: 32px;
  line-height: 44px;
`;

const TheH3 = styled(H3)`
  margin-bottom: 16px;
  padding: 24px 24px 0;
`;

const TheModalH3 = styled(TheH3)`
  padding: 0;
  margin-bottom: 32px;
`;

const Description = styled(Typography)`
  font-size: 14px;
  line-height: 21px;
  color: ${props => props.theme.grayTextColor};
`;

const TheLeftTableCell = styled(TableCell)`
  padding-left: 24px;
`;

export default Members;
